import { Component, Input, OnDestroy, ViewChild, ViewChildren } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CurrentUserService } from '@app/core/authentication/current.user';
import { PermissionsPermissions } from '@app/core/permissions';
import { NavigationService } from '@app/core/services/navigation/navigation.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import { UserCacheService } from '@app/core/services/user-cache/user-cache.service';
import { BusinessEntitiesLogicService, IBusinessEntityDto } from '@app/logic/business-entity';
import { IBusinessEntityMappedItem } from '@app/logic/business-entity/interfaces/i.business-entity.mapped';
import { UsersLogicService } from '@app/logic/users/users.logic.service';
import { BaseSearchViewDirective, SearchFilters } from '@app/shared/base-views/base-search-view.directive';
import { FormMode } from '@app/shared/enums/form';
import { FeatureToggleStatesService } from '@app/core/services/feature-toggle-states/feature-toggle-states.service';
import { IActiveIdAndNameDto, IAddressDto } from '@classictechsolutions/hubapi-transpiled-enums';
import { take } from 'rxjs';

@Component({
    selector: 'cb-business-entities',
    templateUrl: './business-entities.component.html',
    styleUrls: ['./business-entities.component.scss'],
    providers: [BusinessEntitiesLogicService, UsersLogicService],
    standalone: false
})
export class BusinessEntitiesComponent extends BaseSearchViewDirective<any> implements OnDestroy {
    public formMode: FormMode;
    public searchResults: IActiveIdAndNameDto[] = [];
    public activityCode: string;
    public activityName: string;
    public selectedActivity: IActiveIdAndNameDto;
    public regions: IBusinessEntityDto[];
    public isSupplierTypesEnabled: boolean;
    @Input() public mappedItem: IBusinessEntityMappedItem;
    public isElasticSearch = false;
    public get businessEntityForm(): NgForm {
        return this.businessEntityFormParent.first;
    }
    public sideBarBusinessEntityCode: string;
    public sideBarBusinessEntityName: string;
    @ViewChildren('businessEntityForm') public businessEntityFormParent: any;
    @ViewChild('infiniteScrollContainerBusinessEntities', { static: true }) public infiniteScrollContainer: { nativeElement: { scrollHeight: number } };

    public readonly businessEntitiesColumns: string[] = ['Business Entity Name', 'Active', ''];

    constructor(
        public readonly navigationService: NavigationService,
        public readonly toastService: ToastService,
        public readonly currentUser: CurrentUserService,
        public readonly userCacheService: UserCacheService,
        public readonly permissionsPermissions: PermissionsPermissions,
        public readonly dialog: MatDialog,
        private readonly businessEntitiesLogicService: BusinessEntitiesLogicService,
        private readonly featureToggleService: FeatureToggleStatesService
    ) {
        super(
            businessEntitiesLogicService,
            toastService,
            userCacheService
        );
        this.formMode = FormMode.View;
        featureToggleService.init().then(() => {
            this.isSupplierTypesEnabled = featureToggleService.isSupplierTypesEnabled;
        });
        this.businessEntitiesLogicService.getSkinnyBusinessEntityDetails().pipe(take(1)).subscribe(x => {
            this.searchResults = this.sortSearchResults(x);
        });
    }

    protected getExtraSearchFilters(): any {
        return {};
    }

    public formDirty = (): boolean => {
        if (!this.businessEntityForm) {
            return false;
        } else {
            return this.businessEntityForm.dirty;
        }
    };

    public formValid = (): boolean => {
        if (!this.businessEntityForm) {
            return false;
        } else {
            return this.businessEntityForm.valid;
        }
    };

    public saveMethod(): any {
        return this.businessEntitiesLogicService.editBusinessEntity(this.mappedItem.$getMappedDtoItem());
    }

    public goBack(): void {
        this.businessEntityForm.form.reset({ physicalAddress: {}, postalAddress: {} });
        this.mappedItem = undefined;
        this.formMode = FormMode.View;
    }

    public newBusinessEntityClicked(): void {
        this.mappedItem = this.businessEntitiesLogicService.$createMappedItem({ physicalAddress: {} as IAddressDto, postalAddress: {} as IAddressDto, });
        this.sideBarBusinessEntityCode = '';
        this.sideBarBusinessEntityName = '';
        this.formMode = FormMode.Edit;
    }

    public generateReport(): void {
        this.businessEntitiesLogicService
            .generateReport()
            .pipe(take(1)).subscribe({ next: undefined, error: this.handleError });
    }

    protected handleError = (err): void => {
        console.error(err);
    };

    public handleSaveSuccess = (_: IBusinessEntityDto): void => {
        this.businessEntityForm.form.markAsPristine();
        this.formMode = FormMode.View;
        this.businessEntitiesLogicService.$getList().pipe(take(1)).subscribe(x => {
            this.searchResults = this.sortSearchResults(x);
        });
    };

    public sortSearchResults = (searchResults: Array<IActiveIdAndNameDto>): Array<IActiveIdAndNameDto> => {
        return searchResults.sort((a, b) => a.name.localeCompare(b.name));
    };

    public applySavedSearchFiltersToComponent = (searchFilters: SearchFilters): void => {
        this.activityCode = searchFilters.activityCode;
        this.activityName = searchFilters.activityName;
    };

    public editClicked(businessEntity: IActiveIdAndNameDto): void {
        this.selectedActivity = businessEntity;

        this.businessEntitiesLogicService
            .$getItem(this.selectedActivity.id)
            .pipe(take(1)).subscribe(x => {
                this.mappedItem = this.businessEntitiesLogicService
                    .$createMappedItem(x);
                this.sideBarBusinessEntityName = x.name;
            });
        this.formMode = FormMode.Edit;
    }
}
